import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as FileSaver from "file-saver";
import { showLoader, hideLoader } from "./LoaderSlice";
import { showMessage } from "../../helpers";
import {
  adminGetFilingPlans,
  adminPostFilingPlans,
  approveFilingPlans,
  downloadFilingPlansReport,
  getFilingPlans,
  getFilingPlansReport,
  markFilingPlansMessageRead,
  postFilingPlans,
  submitFilingPlans,
} from "../../services/FilingPlans";

// Create async thunks for API calls
export const getFilingPlansData = createAsyncThunk(
  "filingPlans/getFilingPlansData",
  async ({ onSuccess, quarter_name }, { dispatch }) => {
    dispatch(showLoader());
    try {
      const response = await getFilingPlans(quarter_name);
      dispatch(hideLoader());
      if (onSuccess) {
        onSuccess(response);
      }
      return response || {};
    } catch (error) {
      dispatch(hideLoader());
      console.log(error);
      throw error;
    }
  },
);

export const getAdminFilingPlansReport = createAsyncThunk(
  "filingPlans/getAdminFilingPlansReport",
  async ({ data, onSuccess, signal }, { rejectWithValue }) => {
    try {
      const response = await getFilingPlansReport(data, signal);
      if (onSuccess) {
        onSuccess();
      }
      return response || {};
    } catch (error) {
      if (error && (error.name === "AbortError" || signal?.aborted)) {
        throw error;
      }
      const message =
        (error && (error.message || (typeof error === "string" ? error : null))) ||
        "Failed to load Filing Plans report";
      showMessage("error", message);
      console.log(error);
      return rejectWithValue(message);
    }
  },
);

export const downloadFilingReport = createAsyncThunk(
  "filingPlans/downloadFilingReport",
  async ({ filename, quarter, requestObj, onSuccess }, { dispatch }) => {
    dispatch(showLoader());
    try {
      const response = await downloadFilingPlansReport(quarter, requestObj);
      dispatch(hideLoader());
      FileSaver.saveAs(response, filename);
      if (onSuccess) {
        onSuccess();
      }
      return response;
    } catch (error) {
      dispatch(hideLoader());
      console.log(error);
      throw error;
    }
  },
);

export const postFilingPlansData = createAsyncThunk(
  "filingPlans/postFilingPlansData",
  async ({ data, onSuccess, quarter_name }, { dispatch }) => {
    dispatch(showLoader());
    try {
      const response = await postFilingPlans(data, quarter_name);
      dispatch(hideLoader());
      if (onSuccess) {
        onSuccess();
      }
      return response;
    } catch (error) {
      dispatch(hideLoader());
      showMessage("error", error.toString());
      throw error;
    }
  },
);

export const getAdminFilingPlansData = createAsyncThunk(
  "filingPlans/getAdminFilingPlansData",
  async ({ id, onSuccess, quarter_name }, { dispatch }) => {
    dispatch(showLoader());
    try {
      const response = await adminGetFilingPlans(id, quarter_name);
      dispatch(hideLoader());
      if (onSuccess) {
        onSuccess(response);
      }
      return response || {};
    } catch (error) {
      dispatch(hideLoader());
      console.log(error);
      throw error;
    }
  },
);

export const postAdminFilingPlansData = createAsyncThunk(
  "filingPlans/postAdminFilingPlansData",
  async ({ id, data, onSuccess, quarter_name }, { dispatch }) => {
    dispatch(showLoader());
    try {
      const response = await adminPostFilingPlans(id, data, quarter_name);
      dispatch(hideLoader());
      if (onSuccess) {
        onSuccess();
      }
      return response;
    } catch (error) {
      dispatch(hideLoader());
      showMessage("error", error.toString());
      throw error;
    }
  },
);

export const approveFilingPlansReport = createAsyncThunk(
  "filingPlans/approveFilingPlansReport",
  async ({ data, onSuccess }, { dispatch }) => {
    dispatch(showLoader());
    try {
      const response = await approveFilingPlans(data);
      dispatch(hideLoader());
      if (onSuccess) {
        onSuccess();
      }
      return response;
    } catch (error) {
      dispatch(hideLoader());
      showMessage("error", error.toString());
      throw error;
    }
  },
);

export const submitFilingPlansReport = createAsyncThunk(
  "filingPlans/submitFilingPlansReport",
  async ({ data, onSuccess }, { dispatch }) => {
    dispatch(showLoader());
    try {
      const response = await submitFilingPlans(data);
      dispatch(hideLoader());
      if (onSuccess) {
        onSuccess();
      }
      return response;
    } catch (error) {
      dispatch(hideLoader());
      showMessage("error", error.toString());
      throw error;
    }
  },
);

export const partnerMarkFilingPlansMessageRead = createAsyncThunk(
  "filingPlans/partnerMarkFilingPlansMessageRead",
  async ({ type, onSuccess }, { dispatch }) => {
    try {
      const response = await markFilingPlansMessageRead(type);
      if (onSuccess) {
        onSuccess();
      }
      return response;
    } catch (error) {
      showMessage("error", error.toString());
      throw error;
    }
  },
);

const initialState = {
  plansList: {},
  plansReportData: {},
  isLoaded: false,
  reportError: null,
};

const filingPlansSlice = createSlice({
  name: "filingPlans",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Get Filing Plans
      .addCase(getFilingPlansData.pending, (state) => {
        state.isLoaded = false;
      })
      .addCase(getFilingPlansData.fulfilled, (state, action) => {
        state.isLoaded = true;
        state.plansList = action.payload;
      })
      .addCase(getFilingPlansData.rejected, (state) => {
        state.isLoaded = true;
      })

      // Get Filing Plans Report
      .addCase(getAdminFilingPlansReport.pending, (state) => {
        state.isLoaded = false;
        state.reportError = null;
      })
      .addCase(getAdminFilingPlansReport.fulfilled, (state, action) => {
        state.isLoaded = true;
        state.plansReportData = action.payload;
        state.reportError = null;
      })
      .addCase(getAdminFilingPlansReport.rejected, (state, action) => {
        // Ignore aborts caused by component unmount or a newer in-flight request.
        if (action.error?.name === "AbortError") {
          return;
        }
        state.isLoaded = true;
        state.reportError = action.payload || action.error?.message || "Failed to load Filing Plans report";
      })

      // Get Admin Filing Plans
      .addCase(getAdminFilingPlansData.pending, (state) => {
        state.isLoaded = false;
      })
      .addCase(getAdminFilingPlansData.fulfilled, (state, action) => {
        state.isLoaded = true;
        state.plansList = action.payload;
      })
      .addCase(getAdminFilingPlansData.rejected, (state) => {
        state.isLoaded = true;
      });

    // Other actions don't modify state significantly,
    // so we're not adding specific handlers for them
  },
});

export default filingPlansSlice.reducer;
